import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  registrationService,
  type SubscriptionPlan,
  type SubscriptionPrice,
  type CheckEligibilityResponse,
} from "../services/registrationService";
import { getFbc, getFbp } from "../services/metaPixelService";
import { cookieService } from "../services/cookieService";
import { getGAClientId } from "../hooks/useGTM";
import {
  REGISTRATION_SOURCE_STORAGE_KEY,
  captureRegistrationSource,
  getRegistrationSource,
} from "../utils/registrationSourceParams";
import { useRegistrationAnalytics, STEP_LABELS } from "../hooks/useRegistrationAnalytics";
import type { RegistrationErrorType, GrantsScheme } from "../hooks/useRegistrationAnalytics";
import { isTurnstileErrorCode } from "../constants/turnstile";
import { getDocsUrl, getSupportEmail } from "../utils/domainConfig";
import { useTrialDuration } from "../hooks/useTrialDuration";
import { useDetectedBrandLegal } from "../hooks/useDetectedBrandLegal";
import { formatTrialDays } from "../utils/trialDays";
import {
  clearTrialInviteToken,
  parseTrialInviteToken,
  takeTrialInviteRaw,
  type TrialInviteLink,
} from "../utils/trialInviteToken";
import { LegalLink } from "../components/legal/LegalLink";
import {
  LEGAL_VERSION_MISMATCH,
  extractLegalErrorCode,
  mapLegalErrorCode,
} from "../components/legal/legalErrorMapper";
import {
  RegistrationPlanSchemeSelector,
  GRANTS_SCHEME_FALLBACK_LABELS,
} from "../components/RegistrationPlanSchemeSelector";
import { WITHDRAWAL_WAIVER_LABEL } from "@hotelb2b/legal-content";

// ============================================================
// Constants
// ============================================================

/** Total number of steps */
const TOTAL_STEPS = 5;

/** VAT info text shown in plan selection and summary */
const VAT_INFO_TEXT = "Ceny netto. Do każdej ceny zostanie doliczony VAT 23%.";

/** 5b: trial-invite payment info shown in plan selection and summary */
const TRIAL_INVITE_NO_PROFORMA_TEXT =
  "Proformy nie wystawimy automatycznie — zamówisz ją w panelu (Subskrypcja → „Kupuję”), kiedy zdecydujesz.";

/** Allowed Stripe Checkout origins for URL validation (defense-in-depth) */
const STRIPE_ALLOWED_CHECKOUT_ORIGINS = [
  "https://checkout.stripe.com",
  "https://billing.stripe.com",
];

/** Stale time for plans query cache (5 minutes) */
const PLANS_STALE_TIME_MS = 5 * 60 * 1000;

/** AC-9: closed allowlist for `?scheme=` — anything outside it (including absent) falls back to 'all'. */
const GRANTS_SCHEMES: readonly GrantsScheme[] = ["kfs", "bur", "all"];

/**
 * Closed allowlist for `?plan=` (billing interval) forwarded to analytics —
 * symmetric to GRANTS_SCHEMES/AC-6. Sourced from the known values of
 * `SubscriptionPrice.billingInterval` (services/registrationService.ts).
 */
const PLAN_INTERVALS: readonly string[] = ["monthly", "quarterly", "quarterly_3", "yearly"];

// ============================================================
// Helpers
// ============================================================

/**
 * NIP validation - checks if NIP has exactly 10 digits
 */
const validateNIP = (nip: string): string | null => {
  const cleaned = nip.replace(/[^0-9]/g, "");
  if (cleaned.length === 0) return "NIP jest wymagany";
  if (cleaned.length !== 10) {
    return "NIP musi zawierać 10 cyfr";
  }
  return null;
};

/**
 * Phone validation - lightweight client-side check (full E.164 normalization
 * + duplicate detection lives on backend via Phone VO).
 *
 * Accepts common Polish/international input variants:
 *   +48 512 345 678, 0048 512 345 678, 512345678, +48-512-345-678
 *
 * Rules:
 *   - cannot be empty
 *   - after stripping spaces / dashes / parens / leading "+" / leading "00"
 *     must be 9-15 digits (E.164 max length is 15 digits incl. country code)
 *   - cannot contain alphabetic characters
 */
const validatePhone = (phone: string): string | null => {
  const trimmed = phone.trim();
  if (trimmed.length === 0) return "Numer telefonu jest wymagany";
  // Reject any non-digit/space/dash/paren/plus character (e.g. letters)
  if (!/^[+\d\s\-()]+$/.test(trimmed)) {
    return "Nieprawidłowy format numeru telefonu (np. +48 512 345 678)";
  }
  // Normalize: strip non-digits; drop leading "00" (international prefix)
  let digits = trimmed.replace(/[^\d]/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.length < 9 || digits.length > 15) {
    return "Nieprawidłowy format numeru telefonu (np. +48 512 345 678)";
  }
  return null;
};

/** Sentinel value returned by extractErrorMessage for Turnstile cookie/verification failures */
const TURNSTILE_ERROR_SENTINEL = "__TURNSTILE_ERROR__";

/**
 * Extract error message from axios error response.
 * Returns TURNSTILE_ERROR_SENTINEL for Turnstile-specific errors so the UI
 * can render a dedicated message with a docs link instead of raw backend text.
 */
const extractErrorMessage = (err: unknown): string => {
  let errorMessage = "Wystąpił błąd";

  const axiosError = err as {
    response?: { data?: unknown; status?: number };
    message?: string;
  };
  const errorData = axiosError.response?.data;

  // Detect Turnstile-specific 403 errors. We accept both signals:
  // 1. structured errorCode in TURNSTILE_ERROR_CODES (preferred — set by all
  //    backend guards), and
  // 2. raw "Turnstile" substring in the backend message field (defensive
  //    fallback for any path that throws ForbiddenException without errorCode,
  //    or proxy/middleware rewriting the body). Without this fallback, users
  //    saw the raw English string "Turnstile verification failed" instead of
  //    the friendly Polish banner — repro: Agnieszka Lebryk on Safari, 2026-05-08.
  if (axiosError.response?.status === 403 && errorData && typeof errorData === "object") {
    const dataObj = errorData as { errorCode?: string; message?: unknown };
    if (dataObj.errorCode && isTurnstileErrorCode(dataObj.errorCode)) {
      return TURNSTILE_ERROR_SENTINEL;
    }
    if (typeof dataObj.message === "string" && /turnstile/i.test(dataObj.message)) {
      return TURNSTILE_ERROR_SENTINEL;
    }
  }

  if (typeof errorData === "string") {
    errorMessage = errorData;
  } else if (errorData && typeof errorData === "object") {
    const dataObj = errorData as { message?: unknown };
    if (typeof dataObj.message === "string") {
      errorMessage = dataObj.message;
    } else if (
      Array.isArray(dataObj.message) &&
      dataObj.message.length > 0
    ) {
      errorMessage = dataObj.message
        .map((msg: string) => "• " + msg)
        .join("\n");
    } else if (dataObj.message !== undefined) {
      errorMessage = JSON.stringify(dataObj.message);
    }
  } else if (err instanceof Error) {
    errorMessage = err.message;
  } else if (axiosError.message) {
    errorMessage = axiosError.message;
  }

  return errorMessage;
};

/**
 * Validate that a checkout URL points to an allowed Stripe origin
 */
const isValidCheckoutUrl = (url: string): boolean => {
  try {
    return STRIPE_ALLOWED_CHECKOUT_ORIGINS.includes(new URL(url).origin);
  } catch {
    return false;
  }
};

/**
 * AC-9: resolve `?scheme=` through a closed allowlist. An unknown or absent
 * value falls back to 'all' — same pattern as the existing `?plan=` handling
 * (invalid value ignored, never forwarded raw to state or analytics, TS-12).
 */
const resolveGrantsScheme = (value: string | null): GrantsScheme => {
  return (GRANTS_SCHEMES as readonly string[]).includes(value ?? "")
    ? (value as GrantsScheme)
    : "all";
};

/**
 * LOW (review finding, symmetric to AC-6/resolveGrantsScheme): resolve
 * `?plan=` through a closed allowlist before it can reach analytics. Unlike
 * `scheme` there is no safe default value to fall back to, so an unknown
 * value degrades exactly like an absent one — `null` (same as today's
 * `?plan=` absent behavior, which trackRegistrationStart already turns into
 * `undefined` before sending to GA).
 */
const resolvePlanInterval = (value: string | null): string | null => {
  return value !== null && (PLAN_INTERVALS as readonly string[]).includes(value)
    ? value
    : null;
};

/**
 * The package the customer selected — resolved by `grantsScheme` and NOTHING
 * else.
 *
 * `stripePriceId` cannot identify a package: `/register/plans` puts the very
 * same three literals (`direct_monthly|quarterly|yearly`) on all three
 * packages, because they encode only the billing interval (see
 * `RegistrationController.BILLING_INTERVALS`). Every lookup that searched
 * `plans` by `stripePriceId` therefore returned the FIRST package in the
 * response (`SCHEME_PACKAGES` order → Standard KFS) whatever the customer had
 * picked — which is how step 5 came to confirm a package and a price the
 * customer never chose, while `registerDirect` charged the product named by
 * `grantsScheme`.
 *
 * Deliberately returns `undefined` (no `?? plans[0]`) when the scope is not on
 * offer: substituting another package is exactly the silent-mismatch bug.
 */
const findPlanForScheme = (
  plans: SubscriptionPlan[],
  scheme: GrantsScheme
): SubscriptionPlan | undefined =>
  plans.find((p) => (p.grantsScheme ?? "all") === scheme);

/** Package name for display: catalog `displayName`, legacy `name`, then scope. */
const planLabelForScheme = (
  plan: SubscriptionPlan | undefined,
  scheme: GrantsScheme
): string =>
  plan?.displayName ?? plan?.name ?? GRANTS_SCHEME_FALLBACK_LABELS[scheme];

/**
 * Format price for display, applying promotion if active
 */
const displayPrice = (price: SubscriptionPrice): string => {
  if (price.promotion?.isActive) {
    return registrationService.formatPrice(
      price.promotion.promotionalAmount,
      price.currency
    );
  }
  return registrationService.formatPrice(price.amount, price.currency);
};

// ============================================================
// Wizard Form State Types
// ============================================================

export interface WizardFormData {
  // Step 1 - Eligibility
  email: string;
  nip: string;
  phone: string;
  // Step 2 - Company
  companyName: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  billingEmail: string;
  // Step 3 - Account
  firstName: string;
  lastName: string;
  password: string;
  // Step 4 - Plan
  stripePriceId: string;
  /** AC-9: registration scope (KFS-only / BUR-only / both). Preselected from `?scheme=`. */
  grantsScheme: GrantsScheme;
  // GUS metadata
  gusLookupSucceeded: boolean;
  // Step 5 - Optional newsletter opt-in (RODO: separate from required consents)
  newsletterConsent?: boolean;
}

const INITIAL_FORM_DATA: WizardFormData = {
  email: "",
  nip: "",
  phone: "",
  companyName: "",
  street: "",
  city: "",
  postalCode: "",
  country: "PL",
  billingEmail: "",
  firstName: "",
  lastName: "",
  password: "",
  stripePriceId: "",
  grantsScheme: "all",
  gusLookupSucceeded: false,
  newsletterConsent: false,
};

// ============================================================
// Stepper Component (AC-22, AC-28)
// ============================================================

interface StepperProps {
  currentStep: number;
}

const Stepper: React.FC<StepperProps> = ({ currentStep }) => {
  return (
    <nav
      data-testid="registration-stepper"
      aria-label="Kroki rejestracji"
      className="mb-8"
    >
      <ol className="flex items-center justify-between">
        {STEP_LABELS.map((label, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;

          return (
            <li
              key={stepNumber}
              className="flex flex-col items-center flex-1"
              aria-current={isActive ? "step" : undefined}
              aria-label={`Krok ${stepNumber} z ${TOTAL_STEPS}: ${label}`}
            >
              <div className="flex items-center w-full">
                {index > 0 && (
                  <div
                    className={`h-0.5 flex-1 ${
                      isCompleted || isActive
                        ? "bg-primary-700"
                        : "bg-gray-300"
                    }`}
                    aria-hidden="true"
                  />
                )}
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                    isActive
                      ? "bg-primary-700 text-white ring-2 ring-primary-600 ring-offset-1"
                      : isCompleted
                        ? "bg-primary-700 text-white"
                        : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {isCompleted ? (
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    stepNumber
                  )}
                </div>
                {index < STEP_LABELS.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 ${
                      isCompleted ? "bg-primary-700" : "bg-gray-300"
                    }`}
                    aria-hidden="true"
                  />
                )}
              </div>
              <span
                className={`mt-2 text-xs text-center hidden sm:block ${
                  isActive
                    ? "text-primary-700 font-semibold"
                    : isCompleted
                      ? "text-primary-700"
                      : "text-gray-500"
                }`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

// ============================================================
// Error Banner Component (AC-26)
// ============================================================

interface ErrorBannerProps {
  error: string | null;
  /**
   * Scroll the banner into view and focus it whenever a new error appears —
   * for steps whose submit button sits far below the banner (step 5).
   */
  focusOnError?: boolean;
  /** Actions rendered under the message (e.g. invite errors, review MEDIUM-8). */
  children?: React.ReactNode;
}

const ErrorBanner: React.FC<ErrorBannerProps> = ({
  error,
  focusOnError = false,
  children,
}) => {
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!focusOnError || !error || !bannerRef.current) return;
    bannerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    bannerRef.current.focus({ preventScroll: true });
  }, [error, focusOnError]);

  if (!error) return null;

  const isTurnstile = error === TURNSTILE_ERROR_SENTINEL;
  const docsUrl = getDocsUrl("/troubleshooting/weryfikacja-przegladarki");
  const supportEmail = getSupportEmail();

  return (
    <div
      ref={bannerRef}
      tabIndex={focusOnError ? -1 : undefined}
      role="alert"
      aria-live="assertive"
      className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded whitespace-pre-line flex items-start gap-3 mb-6 focus:outline-none focus:ring-2 focus:ring-red-400"
    >
      <svg
        className="w-5 h-5 mt-0.5 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
      <div className="flex-1">
        {isTurnstile ? (
          <>
            Weryfikacja przeglądarki nieudana. Upewnij się, że masz włączone
            ciasteczka (cookie) i nie korzystasz z trybu incognito. Jeśli
            problem nie ustępuje, spróbuj otworzyć stronę w innej przeglądarce
            (Chrome, Firefox, Edge).{" "}
            {docsUrl && (
              <>
                <a
                  href={docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  Dowiedz się więcej
                </a>
                {" | "}
              </>
            )}
            <a
              href={`mailto:${supportEmail}`}
              className="underline font-medium"
            >
              {supportEmail}
            </a>
          </>
        ) : (
          error
        )}
        {children}
      </div>
    </div>
  );
};

// ============================================================
// Trial Invite Problem (5b, review MEDIUM-1)
// ============================================================

/**
 * Why the invite link cannot be used: `malformed` — not a token at all (seen
 * locally); `invalid` — the pre-check says unknown, used or expired.
 */
type TrialInviteProblemKind = "malformed" | "invalid";

const TRIAL_INVITE_PROBLEM_MESSAGES: Record<TrialInviteProblemKind, string> = {
  malformed:
    "Link zaproszenia jest uszkodzony — skopiuj go w całości albo skontaktuj się z osobą, która go wysłała.",
  invalid:
    "Link zaproszenia jest nieprawidłowy, wygasł albo został już użyty. Poproś osobę, która go wysłała, o nowy link.",
};

const TRIAL_INVITE_PROBLEM_ERROR_TYPES: Record<
  TrialInviteProblemKind,
  RegistrationErrorType
> = {
  malformed: "invite_malformed",
  invalid: "invite_invalid",
};

/**
 * Backend messages for an unusable invite at create-direct — verbatim copies
 * of the constants in apps/backend/src/subscriptions/services/
 * trial-invite.service.ts. AllExceptionsFilter passes only `message`, so the
 * text is the only signal.
 */
const TRIAL_INVITE_INVALID_MESSAGE =
  "Link zaproszenia jest nieprawidłowy, wygasł albo został już użyty.";
const TRIAL_INVITE_EMAIL_MISMATCH_MESSAGE =
  "Ten link zaproszenia jest przypisany do innego adresu e-mail.";

/** Why create-direct refused the invite (review MEDIUM-8). */
type TrialInviteErrorKind = "invalid" | "email_mismatch";

const TRIAL_INVITE_ERROR_TYPES: Record<
  TrialInviteErrorKind,
  RegistrationErrorType
> = {
  invalid: "invite_invalid",
  email_mismatch: "invite_email_mismatch",
};

const classifyTrialInviteError = (
  message: string | null
): TrialInviteErrorKind | null => {
  if (message === TRIAL_INVITE_INVALID_MESSAGE) return "invalid";
  if (message === TRIAL_INVITE_EMAIL_MISMATCH_MESSAGE) return "email_mismatch";
  return null;
};

interface TrialInviteProblemProps {
  kind: TrialInviteProblemKind;
  onContinueWithoutInvite: () => void;
}

/**
 * Shown INSTEAD of the wizard step while the invite link is unusable: a lead
 * must not end up in a regular registration (proforma within the hour) without
 * deciding so — "Kontynuuj bez zaproszenia" is that conscious decision.
 */
const TrialInviteProblem: React.FC<TrialInviteProblemProps> = ({
  kind,
  onContinueWithoutInvite,
}) => {
  const supportEmail = getSupportEmail();
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    panelRef.current?.focus();
  }, [kind]);

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      role="alert"
      data-testid="trial-invite-problem"
      className="bg-red-50 border border-red-200 text-red-800 px-4 py-4 rounded space-y-3 focus:outline-none focus:ring-2 focus:ring-red-400"
    >
      <p className="font-semibold">{TRIAL_INVITE_PROBLEM_MESSAGES[kind]}</p>
      <p className="text-sm">
        W razie pytań napisz na{" "}
        <a href={`mailto:${supportEmail}`} className="underline font-medium">
          {supportEmail}
        </a>
        . Możesz też zarejestrować się bez zaproszenia — na zwykłych zasadach,
        bez warunków z zaproszenia.
      </p>
      <button
        type="button"
        onClick={onContinueWithoutInvite}
        data-testid="trial-invite-continue-without"
        className="border border-red-300 bg-white text-red-800 font-semibold py-2 px-4 rounded-lg hover:bg-red-100 transition"
      >
        Kontynuuj bez zaproszenia
      </button>
    </div>
  );
};

interface TrialInviteErrorActionsProps {
  kind: TrialInviteErrorKind;
  onGoToStep1: () => void;
  onContinueWithoutInvite?: () => void;
}

/**
 * The way out of an invite error at the last step (review MEDIUM-8): another
 * e-mail → back to step 1; unusable invite → register without it, knowingly.
 */
const TrialInviteErrorActions: React.FC<TrialInviteErrorActionsProps> = ({
  kind,
  onGoToStep1,
  onContinueWithoutInvite,
}) => {
  const supportEmail = getSupportEmail();
  const buttonClassName =
    "border border-red-300 bg-white text-red-800 font-semibold py-2 px-4 rounded-lg hover:bg-red-100 transition";

  return (
    <div className="mt-3 space-y-3">
      <p className="text-sm">
        {kind === "email_mismatch"
          ? "Wróć do kroku 1 i podaj adres e-mail, dla którego wystawiono zaproszenie. "
          : "Poproś osobę, która wysłała link, o nowy albo zarejestruj się bez zaproszenia — na zwykłych zasadach. "}
        W razie pytań napisz na{" "}
        <a href={`mailto:${supportEmail}`} className="underline font-medium">
          {supportEmail}
        </a>
        .
      </p>
      {kind === "email_mismatch" ? (
        <button type="button" onClick={onGoToStep1} className={buttonClassName}>
          Wróć do kroku 1
        </button>
      ) : (
        onContinueWithoutInvite && (
          <button
            type="button"
            onClick={onContinueWithoutInvite}
            className={buttonClassName}
          >
            Kontynuuj bez zaproszenia
          </button>
        )
      )}
    </div>
  );
};

// ============================================================
// Spinner Component
// ============================================================

const Spinner: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg
    className={`animate-spin ${className}`}
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// ============================================================
// Step 1: Eligibility Check (AC-1 through AC-6)
// ============================================================

interface Step1Props {
  formData: WizardFormData;
  onChange: (name: keyof WizardFormData, value: string) => void;
  onNext: () => void;
  loading: boolean;
  error: string | null;
  eligibilityError: string | null;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}

const Step1Eligibility: React.FC<Step1Props> = ({
  formData,
  onChange,
  onNext,
  loading,
  error,
  eligibilityError,
  headingRef,
}) => {
  const [nipValidationError, setNipValidationError] = useState<string | null>(
    null
  );
  const [phoneValidationError, setPhoneValidationError] = useState<
    string | null
  >(null);

  // M-UX1: Focus eligibility banner on appearance so SR users hear the error
  // and keyboard users land on the message instead of staying on the previous
  // focused element. Banner uses tabIndex={-1} to be programmatically focusable.
  const eligibilityErrorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (eligibilityError && eligibilityErrorRef.current) {
      eligibilityErrorRef.current.focus();
    }
  }, [eligibilityError]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate NIP format before submitting
    const nipErr = validateNIP(formData.nip);
    setNipValidationError(nipErr);
    if (nipErr) return;
    if (!formData.email) return;

    // Validate phone (required + format)
    const phoneErr = validatePhone(formData.phone);
    setPhoneValidationError(phoneErr);
    if (phoneErr) return;

    onNext();
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="step-1-form"
      className="space-y-6"
    >
      <h2
        ref={headingRef}
        className="text-2xl font-semibold text-gray-900"
        tabIndex={-1}
      >
        Podstawowe informacje
      </h2>
      <p className="text-gray-600">
        Podaj email, NIP i numer telefonu, aby sprawdzić dostępność i pobrać dane firmy z GUS.
      </p>

      <ErrorBanner error={error} />

      {/* AC-3: Eligibility error (email/NIP/phone taken) */}
      {eligibilityError && (
        <div
          ref={eligibilityErrorRef}
          tabIndex={-1}
          data-testid="eligibility-error"
          className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded focus:outline-none focus:ring-2 focus:ring-red-400"
          role="alert"
          aria-live="assertive"
        >
          <p className="mb-2">{eligibilityError}</p>
          <a
            href="/login"
            className="inline-flex items-center text-primary-700 hover:text-primary-800 font-medium"
          >
            Przejdź do logowania
            <svg
              className="w-4 h-4 ml-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </a>
        </div>
      )}

      <div className="space-y-4">
        {/* AC-1: Email field */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email *
          </label>
          <input
            id="email"
            type="email"
            name="email"
            autoFocus
            autoComplete="email"
            value={formData.email}
            onChange={(e) => onChange("email", e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="twoj@email.pl"
          />
        </div>

        {/* AC-1: NIP field */}
        <div>
          <label
            htmlFor="nip"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            NIP *
          </label>
          <input
            id="nip"
            type="text"
            name="nip"
            inputMode="numeric"
            value={formData.nip}
            onChange={(e) => {
              onChange("nip", e.target.value);
              setNipValidationError(null);
            }}
            required
            className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              nipValidationError ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="1234567890"
            aria-describedby={nipValidationError ? "nip-error" : undefined}
            aria-invalid={nipValidationError ? "true" : undefined}
          />
          {nipValidationError && (
            <p
              id="nip-error"
              className="text-sm text-red-600 mt-1"
              role="alert"
            >
              {nipValidationError}
            </p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Akceptujemy formaty: 1234567890, 123-456-78-90, PL1234567890
          </p>
        </div>

        {/* Phone field (required) */}
        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Telefon *
          </label>
          <input
            id="phone"
            type="tel"
            name="phone"
            inputMode="tel"
            autoComplete="tel"
            value={formData.phone}
            onChange={(e) => {
              onChange("phone", e.target.value);
              setPhoneValidationError(null);
            }}
            required
            className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              phoneValidationError ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="+48 512 345 678"
            aria-describedby={
              phoneValidationError ? "phone-hint phone-error" : "phone-hint"
            }
            aria-invalid={phoneValidationError ? "true" : undefined}
          />
          {/* M-UX3: keep hint in DOM even when error is visible so SR users
              correcting the number still hear the format guidance. Sighted
              users only see the error (hint is sr-only). */}
          <p
            id="phone-hint"
            className={
              phoneValidationError
                ? "sr-only"
                : "text-sm text-gray-500 mt-1"
            }
          >
            Format międzynarodowy, np. +48 512 345 678
          </p>
          {phoneValidationError && (
            <p
              id="phone-error"
              className="text-sm text-red-600 mt-1"
              role="alert"
            >
              {phoneValidationError}
            </p>
          )}
        </div>
      </div>

      {/* AC-2, AC-6: Next button with loading */}
      <button
        type="submit"
        data-testid="step-1-next"
        disabled={
          loading || !formData.email || !formData.nip || !formData.phone
        }
        aria-busy={loading}
        className="w-full bg-primary-700 hover:bg-primary-800 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition"
      >
        {loading ? (
          <span className="inline-flex items-center justify-center">
            <Spinner className="w-5 h-5 mr-2" />
            Sprawdzanie...
          </span>
        ) : (
          "Dalej"
        )}
      </button>
    </form>
  );
};

// ============================================================
// Step 2: Company Data (AC-7 through AC-9)
// ============================================================

interface Step2Props {
  formData: WizardFormData;
  onChange: (name: keyof WizardFormData, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  gusWarning: string | null;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}

const Step2CompanyData: React.FC<Step2Props> = ({
  formData,
  onChange,
  onNext,
  onBack,
  gusWarning,
  headingRef,
}) => {
  const [formErrors, setFormErrors] = useState<{
    street?: string;
    city?: string;
    postalCode?: string;
  }>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const errors: { street?: string; city?: string; postalCode?: string } = {};

    if (!formData.street.trim()) {
      errors.street = "Ulica jest wymagana";
    }
    if (!formData.city.trim()) {
      errors.city = "Miasto jest wymagane";
    }
    if (!formData.postalCode.trim()) {
      errors.postalCode = "Kod pocztowy jest wymagany";
    } else if (!/^\d{2}-\d{3}$/.test(formData.postalCode.trim())) {
      errors.postalCode = "Nieprawidłowy format kodu pocztowego (XX-XXX)";
    }

    if (Object.keys(errors).length > 0 || !formData.companyName) {
      setFormErrors(errors);
      return;
    }

    // Frontend enforces address as required (AC-3) even though backend API accepts empty values.
    // This is intentional: we want complete billing data for proforma generation.
    onNext();
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="step-2-form"
      className="space-y-6"
    >
      <h2
        ref={headingRef}
        className="text-2xl font-semibold text-gray-900"
        tabIndex={-1}
      >
        Dane firmowe
      </h2>
      <p className="text-gray-600">
        Sprawdź i uzupełnij dane firmy. Pola zostały wstępnie wypełnione na
        podstawie danych z GUS.
      </p>

      {/* AC-5: GUS warning */}
      {gusWarning && (
        <div
          data-testid="gus-warning"
          className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded"
          role="status"
          aria-live="polite"
        >
          {gusWarning}
        </div>
      )}

      <div className="space-y-4">
        {/* AC-7: companyName */}
        <div>
          <label
            htmlFor="companyName"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Nazwa firmy *
          </label>
          <input
            id="companyName"
            type="text"
            name="companyName"
            autoComplete="organization"
            value={formData.companyName}
            onChange={(e) => onChange("companyName", e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Nazwa firmy"
          />
        </div>

        {/* AC-7: street */}
        <div>
          <label
            htmlFor="street"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Ulica *
          </label>
          <input
            id="street"
            type="text"
            name="street"
            autoComplete="street-address"
            value={formData.street}
            onChange={(e) => {
              onChange("street", e.target.value);
              setFormErrors((prev) => ({ ...prev, street: undefined }));
            }}
            required
            className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              formErrors.street ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="ul. Marszałkowska 10/5"
            aria-describedby={formErrors.street ? "street-error" : undefined}
            aria-invalid={formErrors.street ? "true" : undefined}
          />
          {formErrors.street && (
            <p
              id="street-error"
              className="text-sm text-red-600 mt-1"
              role="alert"
            >
              {formErrors.street}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* AC-7: city */}
          <div>
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Miasto *
            </label>
            <input
              id="city"
              type="text"
              name="city"
              autoComplete="address-level2"
              value={formData.city}
              onChange={(e) => {
                onChange("city", e.target.value);
                setFormErrors((prev) => ({ ...prev, city: undefined }));
              }}
              required
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                formErrors.city ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Warszawa"
              aria-describedby={formErrors.city ? "city-error" : undefined}
              aria-invalid={formErrors.city ? "true" : undefined}
            />
            {formErrors.city && (
              <p
                id="city-error"
                className="text-sm text-red-600 mt-1"
                role="alert"
              >
                {formErrors.city}
              </p>
            )}
          </div>

          {/* AC-7: postalCode */}
          <div>
            <label
              htmlFor="postalCode"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Kod pocztowy *
            </label>
            <input
              id="postalCode"
              type="text"
              name="postalCode"
              autoComplete="postal-code"
              value={formData.postalCode}
              onChange={(e) => {
                onChange("postalCode", e.target.value);
                setFormErrors((prev) => ({ ...prev, postalCode: undefined }));
              }}
              required
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                formErrors.postalCode ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="00-001"
              aria-describedby={formErrors.postalCode ? "postalCode-error" : undefined}
              aria-invalid={formErrors.postalCode ? "true" : undefined}
            />
            {formErrors.postalCode && (
              <p
                id="postalCode-error"
                className="text-sm text-red-600 mt-1"
                role="alert"
              >
                {formErrors.postalCode}
              </p>
            )}
          </div>
        </div>

        {/* AC-7: country (read-only, default "PL") */}
        <div>
          <label
            htmlFor="country"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Kraj
          </label>
          <input
            id="country"
            type="text"
            value={formData.country}
            readOnly
            className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        </div>

        {/* AC-9: billingEmail with helper */}
        <div>
          <label
            htmlFor="billingEmail"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email do faktur
          </label>
          <input
            id="billingEmail"
            type="email"
            name="billingEmail"
            aria-describedby="billingEmail-hint"
            value={formData.billingEmail}
            onChange={(e) => onChange("billingEmail", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={formData.email}
          />
          <p id="billingEmail-hint" className="text-sm text-gray-500 mt-1">
            Adres na który będziemy wysyłać faktury (domyślnie email konta)
          </p>
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          data-testid="step-2-back"
          className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-50 transition"
        >
          Wstecz
        </button>
        <button
          type="submit"
          data-testid="step-2-next"
          disabled={!formData.companyName || !formData.street || !formData.city || !formData.postalCode}
          className="flex-1 bg-primary-700 hover:bg-primary-800 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition"
        >
          Dalej
        </button>
      </div>
    </form>
  );
};

// ============================================================
// Step 3: Account Data (AC-10 through AC-12)
// ============================================================

interface Step3Props {
  formData: WizardFormData;
  onChange: (name: keyof WizardFormData, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}

const Step3AccountData: React.FC<Step3Props> = ({
  formData,
  onChange,
  onNext,
  onBack,
  headingRef,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.password ||
      formData.password.length < 8
    )
      return;
    onNext();
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="step-3-form"
      className="space-y-6"
    >
      <h2
        ref={headingRef}
        className="text-2xl font-semibold text-gray-900"
        tabIndex={-1}
      >
        Dane konta
      </h2>

      <div className="space-y-4">
        {/* AC-11: Email read-only */}
        <div>
          <label
            htmlFor="emailDisplay"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email
          </label>
          <input
            id="emailDisplay"
            type="email"
            value={formData.email}
            readOnly
            autoComplete="off"
            className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
          />
          <p className="text-sm text-gray-500 mt-1">
            Ustawiony w kroku 1 - nie można zmienić
          </p>
        </div>

        {/* AC-10: firstName, lastName */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Imię *
            </label>
            <input
              id="firstName"
              type="text"
              name="firstName"
              autoComplete="given-name"
              value={formData.firstName}
              onChange={(e) => onChange("firstName", e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nazwisko *
            </label>
            <input
              id="lastName"
              type="text"
              name="lastName"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={(e) => onChange("lastName", e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* AC-10: password (min 8 chars) */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Hasło *
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => onChange("password", e.target.value)}
              required
              minLength={8}
              maxLength={128}
              aria-describedby="password-hint"
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Ukryj hasło" : "Pokaż hasło"}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          <p id="password-hint" className="text-sm text-gray-500 mt-1">Minimum 8 znaków</p>
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          data-testid="step-3-back"
          className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-50 transition"
        >
          Wstecz
        </button>
        <button
          type="submit"
          data-testid="step-3-next"
          disabled={
            !formData.firstName ||
            !formData.lastName ||
            formData.password.length < 8
          }
          className="flex-1 bg-primary-700 hover:bg-primary-800 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition"
        >
          Dalej
        </button>
      </div>
    </form>
  );
};

// ============================================================
// Step 4: Plan Selection (AC-13 through AC-17)
// ============================================================

/**
 * 5b: a trial invite the backend will honour for this registration (a valid-
 * looking `?invite=` token on the direct path). The invite trial has no
 * automatic proforma and its own length, so steps 4 and 5 must not show the
 * product's trial length or the proforma flow.
 *
 * `trialDays` is the invite's length from POST /register/trial-invite/check
 * (review MEDIUM-8); `null` while the check is pending or unavailable (429,
 * network, 5xx), which renders neutral copy.
 */
export interface TrialInviteInfo {
  trialDays: number | null;
}

interface Step4Props {
  formData: WizardFormData;
  plans: SubscriptionPlan[];
  plansLoading: boolean;
  plansError: boolean;
  trialDurationDays: number | null;
  /** Per-user trial eligibility from step 1. null = not checked → assume eligible. */
  trialEligible: boolean | null;
  /**
   * 5b: invite trial. Takes precedence over `trialDurationDays` and over
   * `trialEligible === false` — registerDirect creates the invite trial
   * regardless of eligibility. `null` (default) = regular registration.
   */
  trialInvite?: TrialInviteInfo | null;
  /**
   * The `?scheme=` scope from the landing handoff that `/register/plans` does
   * NOT offer, after `Register` reconciled `formData.grantsScheme` to a package
   * that it does. `null` in the normal case. Rendered as a note, because
   * quietly showing another package's prices under the customer's chosen scope
   * is the bug this whole path exists to prevent.
   */
  unavailableScheme?: GrantsScheme | null;
  onChange: (name: keyof WizardFormData, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  onRetryPlans: () => void;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}

export const Step4PlanSelection: React.FC<Step4Props> = ({
  formData,
  plans,
  plansLoading,
  plansError,
  trialDurationDays,
  trialEligible,
  trialInvite = null,
  unavailableScheme = null,
  onChange,
  onNext,
  onBack,
  onRetryPlans,
  headingRef,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.stripePriceId) return;
    onNext();
  };

  // AC-1/AC-4: package selection never touches stripePriceId — the shared
  // `direct_monthly|quarterly|yearly` literals across all 3 packages mean the
  // previously selected interval stays valid for the newly selected package.
  // Edge case: if no interval is selected yet (e.g. the URL-preselection
  // effect hasn't run), fall back to the new package's first price so the
  // interval radiogroup never ends up with zero radios checked.
  const handleSchemeChange = (scheme: GrantsScheme) => {
    onChange("grantsScheme", scheme);
    if (!formData.stripePriceId) {
      const matchingPlan = plans.find((p) => (p.grantsScheme ?? "all") === scheme);
      const fallbackPrice = matchingPlan?.prices?.[0];
      if (fallbackPrice) {
        onChange("stripePriceId", fallbackPrice.stripePriceId);
      }
    }
  };

  const handlePriceChange = (stripePriceId: string) => {
    onChange("stripePriceId", stripePriceId);
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="step-4-form"
      className="space-y-6"
    >
      <h2
        ref={headingRef}
        className="text-2xl font-semibold text-gray-900"
        tabIndex={-1}
      >
        Wybór planu
      </h2>

      {/* AC-16: Trial info — gated on per-user eligibility from step 1.
          5b invite → invite trial, no automatic proforma (wins over eligibility).
          trialEligible === false → no free trial (immediate payment).
          Otherwise (eligible, or not-yet-checked) → trial with 0 zł now. */}
      {trialInvite ? (
        <div
          className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 space-y-2"
          data-testid="trial-invite-info"
        >
          <p>
            {trialInvite.trialDays !== null ? (
              <>
                <strong>
                  Okres próbny {formatTrialDays(trialInvite.trialDays)} z
                  zaproszenia — teraz płacisz 0 zł.
                </strong>{" "}
                Przez {formatTrialDays(trialInvite.trialDays)} korzystasz z
                pełnych funkcji systemu bez żadnej opłaty.
              </>
            ) : (
              <>
                <strong>
                  Okres próbny z zaproszenia — teraz płacisz 0 zł.
                </strong>{" "}
                Długość okresu próbnego zgodnie z zaproszeniem.
              </>
            )}
          </p>
          <p>
            {TRIAL_INVITE_NO_PROFORMA_TEXT} Jeśli jej nie zamówisz — po
            zakończeniu okresu próbnego stracisz dostęp i na tym się kończy.
          </p>
        </div>
      ) : trialEligible === false ? (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 space-y-2"
          data-testid="no-trial-info"
        >
          <p>
            <strong>
              Rejestracja bez okresu próbnego — płatność wymagana od razu.
            </strong>{" "}
            Ten adres nie kwalifikuje się do darmowego okresu próbnego.
          </p>
          <p>
            Wyślemy fakturę proforma na podany adres email. Dostęp do systemu
            otrzymasz po jej opłaceniu.
          </p>
        </div>
      ) : (
        <div
          className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 space-y-2"
          data-testid="trial-info"
        >
          <p>
            {trialDurationDays !== null ? (
              <>
                <strong>
                  Okres próbny {formatTrialDays(trialDurationDays)} — teraz
                  płacisz 0 zł.
                </strong>{" "}
                Przez {formatTrialDays(trialDurationDays)} korzystasz z pełnych
                funkcji systemu bez żadnej opłaty.
              </>
            ) : (
              <strong>Darmowy okres próbny — teraz nic nie płacisz.</strong>
            )}
          </p>
          <p>
            W trakcie okresu próbnego wyślemy fakturę proforma na podany adres
            email. Jeśli opłacisz ją przed końcem okresu próbnego, przedłużymy
            subskrypcję. Jeśli nie opłacisz — po zakończeniu okresu próbnego
            stracisz dostęp i na tym się kończy.
          </p>
        </div>
      )}

      {/* AC-13, AC-14: Plan cards */}
      {plansLoading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner className="w-8 h-8 text-primary-700" />
          <span className="ml-3 text-gray-600">Ładowanie planów...</span>
        </div>
      ) : plansError ? (
        // HIGH#7: distinct from the "legitimately empty catalog" branch below —
        // a transient backend failure must not read as "no offer available".
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">
            Wystąpił błąd podczas ładowania cennika, spróbuj ponownie za chwilę.
          </p>
          <button
            type="button"
            onClick={onRetryPlans}
            disabled={plansLoading}
            className="text-primary-700 hover:text-primary-800 font-medium disabled:text-gray-400"
          >
            {plansLoading ? "Ładowanie..." : "Spróbuj ponownie"}
          </button>
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">
            Brak dostępnych planów subskrypcji.
          </p>
          <button
            type="button"
            onClick={onRetryPlans}
            disabled={plansLoading}
            className="text-primary-700 hover:text-primary-800 font-medium disabled:text-gray-400"
          >
            {plansLoading ? "Ładowanie..." : "Spróbuj ponownie"}
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Why the price is worth paying at all — the step otherwise opens
              straight into numbers, with nothing to weigh them against. */}
          <div
            className="rounded-lg border border-gray-200 bg-white p-4"
            data-testid="plan-value-proposition"
          >
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-primary-700 mt-0.5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="font-semibold text-gray-900">
                  Oszczędza nawet cały etat
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Ręczne pilnowanie naborów we wszystkich województwach to
                  kilka godzin tygodniowo — i tak łatwo przeoczyć termin.
                  System sprawdza je za Ciebie bez przerwy i wysyła alert, gdy
                  nabór rusza. Twój zespół zajmuje się wnioskami, a nie
                  szukaniem ogłoszeń.
                </p>
              </div>
            </div>
          </div>

          {/* The landing sent a package this catalog does not offer. Say so —
              the alternative is the customer believing the prices below belong
              to the package they clicked. */}
          {unavailableScheme && (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
              role="status"
              data-testid="scheme-unavailable-note"
            >
              {GRANTS_SCHEME_FALLBACK_LABELS[unavailableScheme]} nie jest teraz
              dostępny w rejestracji. Poniżej pokazujemy pakiety, które możesz
              wybrać — sprawdź zakres i cenę przed przejściem dalej.
            </div>
          )}

          {/* AC-1: two-stage picker — package tiles, then the interval grid
              of exclusively the selected package. Extracted so this file
              doesn't grow a third radiogroup implementation inline. */}
          <RegistrationPlanSchemeSelector
            plans={plans}
            selectedScheme={formData.grantsScheme}
            selectedStripePriceId={formData.stripePriceId}
            onSchemeChange={handleSchemeChange}
            onPriceChange={handlePriceChange}
          />

          {/* AC-14: VAT info */}
          <p className="text-sm text-gray-500 text-center">
            {VAT_INFO_TEXT}
          </p>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          data-testid="step-4-back"
          className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-50 transition"
        >
          Wstecz
        </button>
        <button
          type="submit"
          data-testid="step-4-next"
          disabled={!formData.stripePriceId}
          className="flex-1 bg-primary-700 hover:bg-primary-800 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition"
        >
          Dalej
        </button>
      </div>
    </form>
  );
};

// ============================================================
// Step 5: Summary (AC-18 through AC-21)
// ============================================================

interface Step5Props {
  formData: WizardFormData;
  plans: SubscriptionPlan[];
  onSubmit: (newsletterConsent: boolean) => void;
  onBack: () => void;
  onGoToStep: (step: number) => void;
  loading: boolean;
  error: string | null;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  /**
   * Brand-scoped legal URLs (ADR-088, spec 2026-07-06-per-brand-legal-content
   * AC B / H4) — sourced from `GET /brands/detect`, NOT `domainConfig.ts`.
   * `null`/unsafe while loading or on failure -> renders "Link niedostępny"
   * instead of a broken/wrong-brand link (M6).
   */
  termsUrl?: string | null;
  privacyUrl?: string | null;
  /**
   * True while `GET /brands/detect` is still resolving (H8) — renders a
   * calm loading placeholder instead of "Link niedostępny" for `termsUrl`/
   * `privacyUrl` above. Defaults to `false` (treats absence as "already
   * settled", matching pre-H8 behavior for direct props-based rendering).
   */
  legalLoading?: boolean;
  /** 5b: invite trial — no automatic proforma. `null` (default) = regular registration. */
  trialInvite?: TrialInviteInfo | null;
  /** 5b: drop the invite after create-direct refused it (review MEDIUM-8). */
  onContinueWithoutInvite?: () => void;
}

export const Step5Summary: React.FC<Step5Props> = ({
  formData,
  plans,
  onSubmit,
  onBack,
  onGoToStep,
  loading,
  error,
  headingRef,
  termsUrl = null,
  privacyUrl = null,
  legalLoading = false,
  trialInvite = null,
  onContinueWithoutInvite,
}) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  // RODO / consumer-rights: mandatory withdrawal-right waiver (art. 38 pkt 13
  // UPK) — immediate service access ⇒ loss of the 14-day withdrawal right.
  const [withdrawalWaiverAccepted, setWithdrawalWaiverAccepted] =
    useState(false);
  // RODO: newsletter opt-in is optional and defaults to unchecked (no pre-ticking).
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [consentErrors, setConsentErrors] = useState<{
    terms?: string;
    privacy?: string;
    withdrawalWaiver?: string;
  }>({});
  // SR announcement for the toggle action (WCAG 4.1.3 status messages).
  const [toggleAnnouncement, setToggleAnnouncement] = useState("");

  // DECISION #1 (Option A): "Zaznacz wszystkie" covers all consents
  // (terms + privacy + withdrawal waiver + newsletter) and acts as a toggle.
  const allConsentsSelected =
    termsAccepted &&
    privacyAccepted &&
    withdrawalWaiverAccepted &&
    newsletterConsent;

  const handleSelectAll = () => {
    const next = !allConsentsSelected;
    setTermsAccepted(next);
    setPrivacyAccepted(next);
    setWithdrawalWaiverAccepted(next);
    setNewsletterConsent(next);
    if (next) {
      setConsentErrors({});
    }
    setToggleAnnouncement(
      next ? "Wszystkie zgody zaznaczone" : "Zgody odznaczone"
    );
  };

  // The confirmed package is the one the customer's scope names; its price is
  // then looked up WITHIN that package. Searching `plans` (or a flatMap of all
  // their prices) by the shared `direct_*` id resolved to the first package in
  // the response instead — see findPlanForScheme.
  const selectedPlan = findPlanForScheme(plans, formData.grantsScheme);
  const selectedPrice = selectedPlan?.prices.find(
    (p) => p.stripePriceId === formData.stripePriceId
  );

  const isDirectActivation = formData.stripePriceId
    ? registrationService.isDirectPrice(formData.stripePriceId)
    : false;

  // Only an invite registration sends the token, so only it gets these errors.
  const inviteErrorKind = trialInvite ? classifyTrialInviteError(error) : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: {
      terms?: string;
      privacy?: string;
      withdrawalWaiver?: string;
    } = {};
    if (!termsAccepted) {
      errors.terms = "Musisz zaakceptować regulamin.";
    }
    if (!privacyAccepted) {
      errors.privacy = "Musisz zaakceptować politykę prywatności.";
    }
    if (!withdrawalWaiverAccepted) {
      errors.withdrawalWaiver =
        "Musisz wyrazić zgodę na natychmiastowy dostęp do Usługi.";
    }
    if (Object.keys(errors).length > 0) {
      setConsentErrors(errors);
      return;
    }
    setConsentErrors({});
    onSubmit(newsletterConsent);
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="step-5-form"
      className="space-y-6"
    >
      <h2
        ref={headingRef}
        className="text-2xl font-semibold text-gray-900"
        tabIndex={-1}
      >
        Podsumowanie
      </h2>
      <p className="text-gray-600">
        Sprawdź poprawność danych przed zakończeniem rejestracji.
      </p>

      <ErrorBanner error={error} focusOnError>
        {inviteErrorKind && (
          <TrialInviteErrorActions
            kind={inviteErrorKind}
            onGoToStep1={() => onGoToStep(1)}
            onContinueWithoutInvite={onContinueWithoutInvite}
          />
        )}
      </ErrorBanner>

      {/* AC-18: Company data summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Dane firmowe
          </h3>
          {/* AC-19: Edit button */}
          <button
            type="button"
            onClick={() => onGoToStep(2)}
            aria-label="Edytuj dane firmowe"
            className="text-sm text-primary-700 hover:text-primary-800 font-medium"
          >
            Edytuj
          </button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div>
            <dt className="text-gray-500">Nazwa firmy</dt>
            <dd className="text-gray-900 font-medium">
              {formData.companyName}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">NIP</dt>
            <dd className="text-gray-900 font-medium">{formData.nip}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-gray-500">Adres</dt>
            <dd className="text-gray-900 font-medium">
              {[formData.street, formData.postalCode, formData.city]
                .filter(Boolean)
                .join(", ") || "Nie podano"}
            </dd>
          </div>
        </dl>
      </div>

      {/* AC-18: Account data summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Dane konta</h3>
          <button
            type="button"
            onClick={() => onGoToStep(3)}
            aria-label="Edytuj dane konta"
            className="text-sm text-primary-700 hover:text-primary-800 font-medium"
          >
            Edytuj
          </button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div>
            <dt className="text-gray-500">Email konta</dt>
            <dd className="text-gray-900 font-medium">{formData.email}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Email do faktur</dt>
            <dd className="text-gray-900 font-medium">
              {formData.billingEmail || formData.email}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Imię i nazwisko</dt>
            <dd className="text-gray-900 font-medium">
              {formData.firstName} {formData.lastName}
            </dd>
          </div>
        </dl>
      </div>

      {/* AC-18: Plan summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Wybrany plan
          </h3>
          <button
            type="button"
            onClick={() => onGoToStep(4)}
            aria-label="Edytuj wybrany plan"
            className="text-sm text-primary-700 hover:text-primary-800 font-medium"
          >
            Edytuj
          </button>
        </div>
        {selectedPrice && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {selectedPlan && (
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Plan</dt>
                <dd className="text-gray-900 font-medium" data-testid="summary-plan-name">
                  {planLabelForScheme(selectedPlan, formData.grantsScheme)}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-gray-500">Cena</dt>
              <dd className="text-gray-900 font-medium">
                {displayPrice(selectedPrice)} netto
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Okres rozliczeniowy</dt>
              <dd className="text-gray-900 font-medium">
                {selectedPrice.billingIntervalLabel || registrationService.getIntervalName(selectedPrice.interval, selectedPrice.intervalCount)}
              </dd>
            </div>
            {selectedPrice.promotion?.isActive && (
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Informacja o cenie</dt>
                <dd className="text-amber-700 font-medium">
                  {selectedPrice.promotion.label}
                </dd>
              </div>
            )}
            {selectedPrice.promotion?.isActive && selectedPrice.promotion.lowestPrice30Days !== undefined && (
              <div>
                <dt className="text-gray-500">Najniższa cena z 30 dni</dt>
                <dd data-testid="lowest-30-day-step5" className="text-gray-900 font-medium">
                  {registrationService.formatPrice(selectedPrice.promotion.lowestPrice30Days, selectedPrice.currency)} netto
                </dd>
              </div>
            )}
          </dl>
        )}
      </div>

      {/* AC-21: Payment method specific text */}
      {isDirectActivation ? (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <p className="text-primary-800 text-sm">
            {trialInvite ? (
              <>
                Konto zostanie aktywowane natychmiast.{" "}
                {TRIAL_INVITE_NO_PROFORMA_TEXT}
              </>
            ) : (
              <>
                Konto zostanie aktywowane natychmiast. Fakturę proforma wyślemy
                na email do rozliczeń.
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <p className="text-primary-800 text-sm">
            Po kliknięciu zostaniesz przekierowany na stronę Stripe do
            bezpiecznej płatności.
          </p>
        </div>
      )}

      {/* RODO: Legal consent checkboxes (required) + optional newsletter opt-in */}
      <div className="space-y-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            <span aria-hidden="true">* </span>pole wymagane
          </p>
          <button
            type="button"
            onClick={handleSelectAll}
            aria-label={
              allConsentsSelected
                ? "Odznacz wszystkie zgody"
                : "Zaznacz wszystkie zgody"
            }
            aria-pressed={allConsentsSelected}
            data-testid="select-all-consents"
            className="text-sm text-primary-700 hover:underline font-medium min-h-[44px] px-2 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 rounded"
          >
            {allConsentsSelected ? "Odznacz wszystkie" : "Zaznacz wszystkie"}
          </button>
        </div>
        {/* WCAG 4.1.3: announce the toggle outcome to screen-reader users. */}
        <span className="sr-only" aria-live="polite">
          {toggleAnnouncement}
        </span>

        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                if (e.target.checked) {
                  setConsentErrors((prev) => ({ ...prev, terms: undefined }));
                }
              }}
              data-testid="terms-checkbox"
              className="mt-1 min-w-[24px] min-h-[24px]"
              aria-describedby={consentErrors.terms ? "terms-error" : undefined}
              aria-invalid={consentErrors.terms ? "true" : undefined}
            />
            <span className="text-sm text-gray-700">
              Akceptuję{" "}
              <LegalLink
                url={termsUrl}
                label="Regulamin"
                isLoading={legalLoading}
                unavailableTestId="terms-link-unavailable"
                className="text-primary-700 hover:underline font-medium"
                variant="consent"
              />
              {" "}*
            </span>
          </label>
          {consentErrors.terms && (
            <p
              id="terms-error"
              className="text-red-600 text-sm mt-1 ml-8"
              role="alert"
              data-testid="terms-error"
            >
              {consentErrors.terms}
            </p>
          )}
        </div>

        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={privacyAccepted}
              onChange={(e) => {
                setPrivacyAccepted(e.target.checked);
                if (e.target.checked) {
                  setConsentErrors((prev) => ({ ...prev, privacy: undefined }));
                }
              }}
              data-testid="privacy-checkbox"
              className="mt-1 min-w-[24px] min-h-[24px]"
              aria-describedby={
                consentErrors.privacy ? "privacy-error" : undefined
              }
              aria-invalid={consentErrors.privacy ? "true" : undefined}
            />
            <span className="text-sm text-gray-700">
              Akceptuję{" "}
              <LegalLink
                url={privacyUrl}
                label="Politykę prywatności"
                isLoading={legalLoading}
                unavailableTestId="privacy-link-unavailable"
                className="text-primary-700 hover:underline font-medium"
                variant="consent"
              />
              {" "}*
            </span>
          </label>
          {consentErrors.privacy && (
            <p
              id="privacy-error"
              className="text-red-600 text-sm mt-1 ml-8"
              role="alert"
              data-testid="privacy-error"
            >
              {consentErrors.privacy}
            </p>
          )}
        </div>

        {/* RODO / consumer-rights: mandatory withdrawal-right waiver (art. 38 pkt 13 UPK) */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={withdrawalWaiverAccepted}
              onChange={(e) => {
                setWithdrawalWaiverAccepted(e.target.checked);
                if (e.target.checked) {
                  setConsentErrors((prev) => ({
                    ...prev,
                    withdrawalWaiver: undefined,
                  }));
                }
              }}
              data-testid="withdrawal-waiver-checkbox"
              className="mt-1 min-w-[24px] min-h-[24px]"
              aria-describedby={
                consentErrors.withdrawalWaiver
                  ? "withdrawal-waiver-error"
                  : undefined
              }
              aria-invalid={consentErrors.withdrawalWaiver ? "true" : undefined}
            />
            <span className="text-sm text-gray-700">
              {WITHDRAWAL_WAIVER_LABEL} *
            </span>
          </label>
          {consentErrors.withdrawalWaiver && (
            <p
              id="withdrawal-waiver-error"
              className="text-red-600 text-sm mt-1 ml-8"
              role="alert"
              data-testid="withdrawal-waiver-error"
            >
              {consentErrors.withdrawalWaiver}
            </p>
          )}
        </div>

        {/* Optional newsletter opt-in (RODO: not pre-ticked, separate from required consents) */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={newsletterConsent}
              onChange={(e) => setNewsletterConsent(e.target.checked)}
              data-testid="newsletter-checkbox"
              className="mt-1 min-w-[24px] min-h-[24px]"
              aria-describedby="newsletter-consent-desc"
            />
            <span className="text-sm text-gray-700">
              <span className="font-medium">Newsletter (opcjonalnie)</span>
              <span id="newsletter-consent-desc" className="block mt-1">
                Wyrażam zgodę na otrzymywanie od KiedyNabory.pl informacji
                handlowych drogą elektroniczną na podany adres e-mail
                (newsletter). Zgoda jest dobrowolna i można ją wycofać w
                dowolnym momencie.
              </span>
            </span>
          </label>
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          data-testid="step-5-back"
          className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-50 transition"
        >
          Wstecz
        </button>
        {/* AC-20: Submit button */}
        <button
          type="submit"
          data-testid="summary-submit"
          disabled={
            loading ||
            !termsAccepted ||
            !privacyAccepted ||
            !withdrawalWaiverAccepted
          }
          aria-busy={loading}
          className="flex-1 bg-primary-700 hover:bg-primary-800 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition"
        >
          {loading ? (
            <span className="inline-flex items-center justify-center">
              <Spinner className="w-5 h-5 mr-2" />
              Przetwarzanie...
            </span>
          ) : isDirectActivation ? (
            "Załóż konto"
          ) : (
            "Przejdź do płatności"
          )}
        </button>
      </div>

      <p className="text-sm text-gray-500 text-center">
        {VAT_INFO_TEXT}
      </p>
    </form>
  );
};

// ============================================================
// Main Register Component (5-step wizard)
// ============================================================

/**
 * Register Page - 5-Step Registration Wizard
 *
 * Flow:
 * 1. Eligibility Check (email + NIP) - calls POST /register/check-eligibility
 * 2. Company Data (pre-filled from GUS, editable)
 * 3. Account Data (firstName, lastName, password)
 * 4. Plan Selection (from GET /register/plans)
 * 5. Summary + Submit
 *
 * After submit: redirect to Stripe Checkout or direct activation success page.
 *
 * @see docs/specifications/2026-03-12-pa6-registration-flow-ui-redesign.md
 */
export const Register: React.FC = () => {
  // Wizard step state (AC-27: no persistence, no URL param)
  const [currentStep, setCurrentStep] = useState(1);

  // Analytics hook (AC-12: SRP — analytics logic extracted from Register.tsx)
  const analytics = useRegistrationAnalytics();

  // AC-F1: Fetch trial duration from public catalog API (auto-detects brand from hostname)
  const { days: trialDurationDays } = useTrialDuration();

  // ADR-088 / spec 2026-07-06-per-brand-legal-content (AC B, H4): the ONLY
  // source for the Regulamin/Polityka links AND the version submitted at
  // registration is GET /brands/detect — NOT the `@hotelb2b/legal-content`
  // package constant, NOT domainConfig.ts. `refetch` re-runs detection after
  // a LEGAL_VERSION_MISMATCH so a retry submits the now-current version (H8).
  const {
    termsUrl: brandTermsUrl,
    privacyUrl: brandPrivacyUrl,
    termsVersion: brandTermsVersion,
    privacyVersion: brandPrivacyVersion,
    isLoading: brandLegalLoading,
    refetch: refetchBrandLegal,
  } = useDetectedBrandLegal();

  // Per-user trial eligibility from POST /register/check-eligibility (step 1).
  // null = not yet checked → treat as eligible (matches backend default when
  // the trial field is absent). false = no free trial → immediate payment.
  const [trialEligible, setTrialEligible] = useState<boolean | null>(null);

  // Form data across all steps
  const [formData, setFormData] = useState<WizardFormData>(INITIAL_FORM_DATA);

  // 5b: trial invite without proforma — single-use token from an admin link
  // (?invite=...). public/invite-capture.js moves it into sessionStorage and
  // out of the URL before GTM/Sentry read the address (review MEDIUM-2);
  // takeTrialInviteRaw() covers the case the script did not run. Read once on
  // mount through the same charset the backend accepts and never passed to
  // analytics. Sent only on the direct (bank transfer) path, where the backend
  // honours it.
  const [inviteLink, setInviteLink] = useState<TrialInviteLink>(() =>
    parseTrialInviteToken(takeTrialInviteRaw())
  );
  const inviteToken = inviteLink.status === "token" ? inviteLink.token : null;

  // Review MEDIUM-8: ask whether the invite can be used before the lead fills
  // in the wizard (does not consume it). No retry and no refetch — the
  // endpoint is rate-limited per IP; any failure (429, network, 5xx) leaves
  // the verdict unknown and the wizard usable with neutral copy.
  const { data: inviteCheck } = useQuery({
    queryKey: ["trial-invite-check", inviteToken],
    queryFn: () => registrationService.checkTrialInvite(inviteToken as string),
    enabled: inviteToken !== null,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const validInvite = inviteCheck?.valid === true ? inviteCheck : null;

  // Review MEDIUM-1: a damaged link blocks the wizard until the user either
  // fixes the link or consciously continues without the invite — and so does
  // an invite the pre-check reports as unusable (MEDIUM-8).
  let inviteProblem: TrialInviteProblemKind | null = null;
  if (inviteLink.status === "malformed") inviteProblem = "malformed";
  else if (inviteCheck?.valid === false) inviteProblem = "invalid";

  // 5b: the invite applies only where its token is sent — the direct path.
  // No price picked yet means not on the Stripe path either, so it applies.
  // Its length comes from the pre-check; while unknown → neutral copy, never
  // the product's default trial length.
  const trialInvite: TrialInviteInfo | null =
    inviteToken &&
    !inviteProblem &&
    (!formData.stripePriceId ||
      registrationService.isDirectPrice(formData.stripePriceId))
      ? {
          trialDays:
            typeof validInvite?.trialDays === "number"
              ? validInvite.trialDays
              : null,
        }
      : null;

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eligibilityError, setEligibilityError] = useState<string | null>(
    null
  );
  const [gusWarning, setGusWarning] = useState<string | null>(null);

  // Drops the invite knowingly — from the broken/invalid-link panel or after
  // create-direct refused it at step 5.
  const continueWithoutInvite = useCallback(() => {
    clearTrialInviteToken();
    setInviteLink({ status: "absent" });
    setError(null);
  }, []);

  // Step heading refs for focus management (AC-28)
  const stepHeadingRef = useRef<HTMLHeadingElement | null>(null);

  // Query client for prefetching plans
  const queryClient = useQueryClient();

  // TanStack Query for plans (AC-13, cached with staleTime: 5 min)
  const {
    data: plans = [],
    isLoading: plansLoading,
    isError: plansError,
    refetch: refetchPlans,
  } = useQuery({
    queryKey: ["registration-plans"],
    queryFn: () => registrationService.getPlans(),
    staleTime: PLANS_STALE_TIME_MS,
    enabled: currentStep >= 4,
  });

  // HIGH#7: distinguish "catalog fetch failed" from "catalog legitimately
  // empty" — support can't tell those apart from a single generic message.
  // Fire once per failure (not on every re-render) so the funnel isn't spammed.
  const trackedPlansErrorRef = useRef(false);
  useEffect(() => {
    if (plansError && !trackedPlansErrorRef.current) {
      trackedPlansErrorRef.current = true;
      analytics.trackRegistrationError({
        stepNumber: 4,
        errorType: "api_unavailable",
      });
    } else if (!plansError) {
      trackedPlansErrorRef.current = false;
    }
  }, [plansError, analytics]);

  // 5b: report an unusable invite once per kind — the type only, never the token.
  const trackedInviteProblemRef = useRef<TrialInviteProblemKind | null>(null);
  useEffect(() => {
    if (inviteProblem && trackedInviteProblemRef.current !== inviteProblem) {
      trackedInviteProblemRef.current = inviteProblem;
      analytics.trackRegistrationError({
        stepNumber: currentStep,
        errorType: TRIAL_INVITE_PROBLEM_ERROR_TYPES[inviteProblem],
      });
    }
  }, [inviteProblem, currentStep, analytics]);

  /**
   * The `?scheme=` scope the catalog turned out not to offer (see the reconcile
   * effect below). Step 4 renders it as a note; `null` in the normal case.
   */
  const [unavailableScheme, setUnavailableScheme] =
    useState<GrantsScheme | null>(null);

  // AC-17/AC-9: Pre-select plan based on URL parameters ?plan= (interval) and
  // ?scheme= (package, already resolved into formData.grantsScheme by the
  // mount effect below). stripePriceId ("direct_monthly") is shared across
  // all 3 package cards, so matching must stay within the plan whose
  // grantsScheme equals the resolved scope — otherwise this could silently
  // preselect the wrong package's price.
  useEffect(() => {
    if (plans.length > 0 && !formData.stripePriceId) {
      const urlParams = new URLSearchParams(window.location.search);
      const planParam = urlParams.get("plan");
      // No `?? plans[0]`: preselecting another package's price would leave
      // step 4 showing prices that do not belong to `formData.grantsScheme`
      // (the field the charge path reads). The reconcile effect below moves
      // the scope onto an available package first; this effect then runs again.
      const matchingPlan = findPlanForScheme(plans, formData.grantsScheme);

      if (planParam && matchingPlan) {
        // Try to find matching price by billingInterval, within the resolved package
        const matchingPrice = matchingPlan.prices.find(
          (p) => p.billingInterval === planParam
        );

        if (matchingPrice) {
          setFormData((prev) => ({
            ...prev,
            stripePriceId: matchingPrice.stripePriceId,
          }));
          return;
        }
        // AC-17: Invalid value ignored - fall through to default
      }

      // Default: select the first price of the resolved package.
      // `matchingPlan` is now `SubscriptionPlan | undefined` (the `?? plans[0]`
      // fallback is gone), so the guard has to narrow it — `?.length > 0` is a
      // comparison on `number | undefined` and fails the production typecheck
      // (TS18048), which jest/babel and non-type-aware eslint both let through.
      if (matchingPlan && matchingPlan.prices.length > 0) {
        setFormData((prev) => ({
          ...prev,
          stripePriceId: matchingPlan.prices[0].stripePriceId,
        }));
      }
    }
  }, [plans, formData.stripePriceId, formData.grantsScheme]);

  /**
   * Reconcile `?scheme=` against the catalog `/register/plans` actually
   * returned: a scope nobody offers must not survive into the payload, because
   * `registerDirect` resolves the charged product from `grantsScheme` alone
   * (STANDARD_PACKAGE_PRODUCT_ID_BY_SCHEME) while the customer only ever sees
   * the prices of a package that IS rendered. Reconciling here keeps those two
   * the same package; `unavailableScheme` makes the swap visible in step 4.
   */
  useEffect(() => {
    if (plans.length === 0) return;
    if (findPlanForScheme(plans, formData.grantsScheme)) {
      return;
    }
    const availableScheme: GrantsScheme = plans[0].grantsScheme ?? "all";
    setUnavailableScheme(formData.grantsScheme);
    setFormData((prev) => ({ ...prev, grantsScheme: availableScheme }));
  }, [plans, formData.grantsScheme]);

  // AC-28: Focus step heading on step change (skip step 1 - email has autoFocus)
  const prevStep = useRef(currentStep);
  useEffect(() => {
    if (prevStep.current !== currentStep && stepHeadingRef.current) {
      stepHeadingRef.current.focus();
    }
    prevStep.current = currentStep;
  }, [currentStep]);

  // Warn user before leaving page with unsaved data (skip during submit)
  useEffect(() => {
    const hasData = !loading && (formData.email !== "" || formData.nip !== "" || formData.phone !== "" || formData.companyName !== "" || formData.firstName !== "" || formData.password !== "");
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    if (hasData) {
      window.addEventListener("beforeunload", handler);
    }
    return () => window.removeEventListener("beforeunload", handler);
  }, [formData, loading]);

  // Update document title per step for screen readers and tab identification
  useEffect(() => {
    document.title = `Rejestracja - Krok ${currentStep}: ${STEP_LABELS[currentStep - 1]} | KiedyNabory.pl`;
    return () => { document.title = "KiedyNabory.pl"; };
  }, [currentStep]);

  // AC-3: Emit registration_start once on mount (not on re-render)
  useEffect(() => {
    // Registration source: one shared parser (first wins in the session).
    captureRegistrationSource();
    const urlParams = new URLSearchParams(window.location.search);
    // `ref` = internal-link entry attribution from the landing pages (e.g.
    // "barometr-zawodow-voivodeship-mazowieckie"). Persisted to sessionStorage so
    // the success page can attach it to the `sign_up` conversion (signup_entry).
    const refParam = urlParams.get("ref");
    if (refParam) {
      sessionStorage.setItem("registrationRef", refParam);
    }
    // AC-9: resolve `?scheme=` once on mount through the closed allowlist —
    // the raw query value is never stored or forwarded to analytics (TS-12).
    const resolvedScheme = resolveGrantsScheme(urlParams.get("scheme"));
    setFormData((prev) => ({ ...prev, grantsScheme: resolvedScheme }));
    // LOW: `plan` must go through a closed allowlist too — same protection
    // `scheme` already has (AC-9/TS-12), the raw query value never reaches analytics.
    analytics.trackRegistrationStart({
      source: getRegistrationSource({ marketingConsent: false })?.utmSource ?? urlParams.get("utm_source"),
      plan: resolvePlanInterval(urlParams.get("plan")),
      signupEntry: refParam,
      scheme: resolvedScheme,
    });
    // Track initial step view (AC-4)
    analytics.trackStepView(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AC-4: Emit registration_step_view on step change (skip initial — handled above)
  const prevStepForAnalytics = useRef(1);
  useEffect(() => {
    if (prevStepForAnalytics.current !== currentStep) {
      analytics.trackStepView(currentStep);
      prevStepForAnalytics.current = currentStep;
    }
  }, [currentStep, analytics]);

  /**
   * Update form field value.
   *
   * M-UX2: clear stale eligibility error banner when the user edits one of the
   * fields that participates in eligibility check (email/nip/phone). Without
   * this the duplicate-email/NIP/phone banner persists until the next submit,
   * which is asymmetric with phoneValidationError (cleared on onChange) and
   * misleads the user into thinking their edit had no effect.
   */
  const handleFieldChange = useCallback((name: keyof WizardFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "email" || name === "nip" || name === "phone") {
      setEligibilityError(null);
    }
  }, []);

  /**
   * Navigate to a specific step (AC-23: back without data loss, AC-19: edit from summary)
   */
  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= TOTAL_STEPS) {
      setError(null);
      setCurrentStep(step);
    }
  }, []);

  /**
   * Handle Step 1 -> Step 2 transition
   * Calls POST /register/check-eligibility (AC-2)
   */
  const handleStep1Next = useCallback(async () => {
    setLoading(true);
    setError(null);
    setEligibilityError(null);
    setGusWarning(null);

    // Prefetch plans during check-eligibility wait (spec requirement)
    queryClient.prefetchQuery({
      queryKey: ["registration-plans"],
      queryFn: () => registrationService.getPlans(),
      staleTime: PLANS_STALE_TIME_MS,
    });

    try {
      const result: CheckEligibilityResponse =
        await registrationService.checkEligibility({
          email: formData.email,
          nip: formData.nip,
          phone: formData.phone,
        });

      // AC-3: Email or NIP taken
      if (!result.emailAvailable) {
        analytics.trackEligibilityCheck({ result: "email_taken", gusSuccess: false });
        analytics.trackRegistrationError({ stepNumber: 1, errorType: "email_taken" });
        setEligibilityError(
          "Ten adres email jest już zarejestrowany w systemie."
        );
        return;
      }
      if (!result.nipAvailable) {
        analytics.trackEligibilityCheck({ result: "nip_taken", gusSuccess: false });
        analytics.trackRegistrationError({ stepNumber: 1, errorType: "nip_taken" });
        setEligibilityError(
          "Ten NIP jest już zarejestrowany w systemie."
        );
        return;
      }
      if (!result.phoneAvailable) {
        analytics.trackEligibilityCheck({ result: "phone_taken", gusSuccess: false });
        analytics.trackRegistrationError({ stepNumber: 1, errorType: "phone_taken" });
        setEligibilityError(
          "Ten numer telefonu jest już zarejestrowany w systemie. Jeśli to pomyłka, sprawdź numer lub skontaktuj się z nami."
        );
        return;
      }

      // Per-user trial eligibility (present only when registration can
      // proceed). Absent → default to eligible for backward compatibility.
      setTrialEligible(result.trial?.eligible ?? true);

      // AC-4, AC-5: GUS data handling
      const gusSuccess = Boolean(result.gusData?.gusLookupSucceeded);
      analytics.trackEligibilityCheck({ result: "eligible", gusSuccess });

      if (result.gusData) {
        // AC-5: GUS timeout/partial data warning
        if (!result.gusData.gusLookupSucceeded) {
          setGusWarning(
            "Nie udało się pobrać wszystkich danych z GUS. Prosimy o ręczne uzupełnienie."
          );
        }
        // AC-4: Pre-fill whatever data is available
        setFormData((prev) => ({
          ...prev,
          companyName: result.gusData!.companyName || prev.companyName,
          street: result.gusData!.street || prev.street,
          city: result.gusData!.city || prev.city,
          postalCode: result.gusData!.postalCode || prev.postalCode,
          gusLookupSucceeded: result.gusData!.gusLookupSucceeded,
        }));
      } else {
        // GUS data null (should not happen when both available, but handle gracefully)
        setGusWarning(
          "Nie udało się pobrać wszystkich danych z GUS. Prosimy o ręczne uzupełnienie."
        );
      }

      // AC-5: Emit step complete for step 1 (forward navigation)
      analytics.trackStepComplete(1);

      // Proceed to step 2
      setCurrentStep(2);
    } catch (err: unknown) {
      const rawMessage = extractErrorMessage(err);
      const errorType: RegistrationErrorType =
        rawMessage === TURNSTILE_ERROR_SENTINEL
          ? "turnstile_failed"
          : "api_unavailable";
      analytics.trackRegistrationError({ stepNumber: 1, errorType });
      setError(rawMessage);
    } finally {
      setLoading(false);
    }
  }, [formData.email, formData.nip, formData.phone, queryClient, analytics]);

  /**
   * Handle final submission from Step 5 (AC-20)
   */
  const handleFinalSubmit = useCallback(async (newsletterConsent: boolean) => {
    setLoading(true);
    setError(null);

    // ADR-088 (H4): never submit without a confirmed brand-scoped version —
    // the backend would just bounce this off as LEGAL_VERSION_MISMATCH anyway
    // (S1 GET /brands/detect is the single source); failing fast here avoids
    // sending `undefined` and gives a clearer message than a generic API error.
    // H10: does NOT suggest a browser refresh — the wizard has no
    // persistence (AC-27, line ~1886) and a refresh would wipe all 5 steps.
    // Same recovery pattern as the LEGAL_VERSION_MISMATCH catch below:
    // refetch in the background and let the user retry the submit.
    if (!brandTermsVersion || !brandPrivacyVersion) {
      setError(
        "Sprawdzamy aktualną wersję dokumentów, spróbuj ponownie za chwilę."
      );
      void refetchBrandLegal();
      setLoading(false);
      return;
    }

    try {
      const gaClientId = getGAClientId() ?? undefined;
      const fbc = getFbc();
      const fbp = getFbp();
      const marketingConsent = cookieService.hasMarketingConsent();

      const isDirectActivation = registrationService.isDirectPrice(
        formData.stripePriceId
      );

      // Resolve plan details for analytics
      const selectedPlanForAnalytics = findPlanForScheme(
        plans,
        formData.grantsScheme
      );
      const selectedPriceForAnalytics = (
        selectedPlanForAnalytics?.prices ?? []
      )
        .find((p) => p.stripePriceId === formData.stripePriceId);

      // `id`/`name` exist only on the legacy single-plan shape; the 3-package
      // catalog sends `productId`/`displayName`, so both fields reached GA4 as
      // empty strings — the conversion lost the package it was for.
      const selectedPlanIdForAnalytics =
        selectedPlanForAnalytics?.id ?? selectedPlanForAnalytics?.productId ?? "";
      const selectedPlanNameForAnalytics = selectedPlanForAnalytics
        ? planLabelForScheme(selectedPlanForAnalytics, formData.grantsScheme)
        : "";

      // AC-7: Emit registration_submit BEFORE API call
      analytics.trackRegistrationSubmit({
        planId: selectedPlanIdForAnalytics,
        planName: selectedPlanNameForAnalytics,
        billingInterval: selectedPriceForAnalytics?.billingInterval ?? "",
        activationType: isDirectActivation ? "direct" : "stripe",
      });

      // Persist conversion attributes so the success page can attach them to the
      // GA4 `sign_up` conversion (which is synthesized in GTM from the
      // registration_success_view event, fired on /registration/success — a fresh
      // page where this in-memory plan/method context is no longer available).
      sessionStorage.setItem(
        "registrationConversionMeta",
        JSON.stringify({
          method: "email",
          plan_id: selectedPlanIdForAnalytics,
          plan_name: selectedPlanNameForAnalytics,
          billing_interval: selectedPriceForAnalytics?.billingInterval ?? "",
        })
      );

      const source = getRegistrationSource({ marketingConsent });

      // AC-12: companyName maps to tenantName in RegisterTenantDto
      const submitData = {
        tenantName: formData.companyName,
        nip: formData.nip,
        email: formData.email,
        phone: formData.phone,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
        stripePriceId: formData.stripePriceId,
        // AC-9: narrow schemes are only ever paired with a direct_* price
        // (Step4 never offers a Stripe price when grantsScheme != 'all'),
        // so this is always 'all' on the Stripe path — satisfies AC-12.
        grantsScheme: formData.grantsScheme,
        companyName: formData.companyName,
        street: formData.street || undefined,
        city: formData.city || undefined,
        postalCode: formData.postalCode || undefined,
        billingEmail: formData.billingEmail || undefined,
        gusLookupSucceeded: formData.gusLookupSucceeded,
        gaClientId,
        fbc,
        fbp,
        marketingConsent,
        // RODO: Legal consent (required by RegisterTenantDto)
        termsAccepted: true,
        privacyAccepted: true,
        termsVersion: brandTermsVersion,
        privacyVersion: brandPrivacyVersion,
        // RODO / consumer-rights: mandatory withdrawal-right waiver (art. 38
        // pkt 13 UPK). Versionless on the wire — backend stamps the SoT version.
        withdrawalWaiverAccepted: true,
        // Optional newsletter opt-in (default false; backend treats absence as false).
        // Source of truth is Step5Summary's local state, passed in via the onSubmit
        // callback argument — NOT formData.newsletterConsent, which is intentionally
        // left unsynchronized (the field exists on WizardFormData for type/initial
        // shape only and is never written back from step 5).
        newsletterConsent,
        // Registration source (contract.md p. 3); click IDs only with marketing consent.
        ...(source ? { source } : {}),
      };

      if (isDirectActivation) {
        // Direct activation flow (bank transfer, proforma invoice)
        // 5b: invite token (if any) — trial without automatic proforma.
        const response = await registrationService.registerTenantDirect(
          inviteToken ? { ...submitData, inviteToken } : submitData
        );

        // AC-7: Emit sign_up after success (direct flow — no redirect cut-off risk)
        analytics.trackSignUp({
          method: "email",
          planId: selectedPlanIdForAnalytics,
          useBeacon: false,
        });

        // Store email in sessionStorage to avoid exposing PII in URL query parameters
        // (server logs, browser history, Referer header leakage)
        sessionStorage.setItem('registrationEmail', response.email);
        // 5b: the success page must not ask an invite registrant to pay a
        // proforma. Removed on a regular registration so an earlier invite
        // registration in this tab cannot leak into it.
        if (inviteToken) {
          sessionStorage.setItem("registrationTrialInvite", "true");
        } else {
          sessionStorage.removeItem("registrationTrialInvite");
        }
        // The invite is used up — a later /register in this tab starts clean.
        clearTrialInviteToken();
        // The source belongs to this account — the next registration in the tab starts clean.
        sessionStorage.removeItem(REGISTRATION_SOURCE_STORAGE_KEY);
        sessionStorage.setItem("registrationSubdomain", response.subdomain);
        if (response.eventId) {
          sessionStorage.setItem("registrationEventId", response.eventId);
        }
        window.location.href = "/registration/success?direct=true";
      } else {
        // Stripe Checkout flow
        const response =
          await registrationService.registerTenant(submitData);
        // Validate checkout URL origin (defense-in-depth)
        if (!isValidCheckoutUrl(response.checkoutUrl)) {
          setError("Nieprawidłowy URL płatności. Skontaktuj się z obsługą.");
          setLoading(false);
          return;
        }

        // AC-7: Emit sign_up with sendBeacon fallback (Stripe redirect may cut off dataLayer push)
        analytics.trackSignUp({
          method: "email",
          planId: selectedPlanIdForAnalytics,
          useBeacon: true,
        });

        window.location.href = response.checkoutUrl;
      }
    } catch (err: unknown) {
      // H8: LEGAL_VERSION_MISMATCH -> friendly message + refetch the current
      // version/URL (mirrors ReacceptanceModal's onVersionMismatch), instead
      // of the generic error banner.
      const legalErrorCode = extractLegalErrorCode(err);
      if (legalErrorCode === LEGAL_VERSION_MISMATCH) {
        analytics.trackRegistrationError({
          stepNumber: 5,
          errorType: "api_unavailable",
        });
        setError(mapLegalErrorCode(legalErrorCode));
        void refetchBrandLegal();
        setLoading(false);
        return;
      }

      const rawMessage = extractErrorMessage(err);
      // 5b: invite refusals are the lead's situation, not an outage.
      const inviteErrorKind = inviteToken
        ? classifyTrialInviteError(rawMessage)
        : null;
      let errorType: RegistrationErrorType = "api_unavailable";
      if (inviteErrorKind) errorType = TRIAL_INVITE_ERROR_TYPES[inviteErrorKind];
      else if (rawMessage === TURNSTILE_ERROR_SENTINEL) errorType = "turnstile_failed";
      analytics.trackRegistrationError({ stepNumber: 5, errorType });
      setError(rawMessage);
      setLoading(false);
    }
  }, [
    formData,
    plans,
    analytics,
    brandTermsVersion,
    brandPrivacyVersion,
    refetchBrandLegal,
    inviteToken,
  ]);

  // ============================================================
  // Render
  // ============================================================

  // An unusable invite replaces the step content until the user decides.
  const visibleStep = inviteProblem ? null : currentStep;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Rejestracja KiedyNabory.pl
          </h1>
          <p className="text-lg text-gray-600">
            Wypełnij formularz i rozpocznij korzystanie z systemu
          </p>
          {trialInvite && (
            <div
              className="mt-4 text-sm text-primary-800 bg-primary-50 border border-primary-200 rounded-lg px-4 py-2 space-y-1"
              role="status"
              data-testid="trial-invite-banner"
            >
              <p>
                {trialInvite.trialDays !== null
                  ? `Rejestrujesz się z zaproszenia na ${formatTrialDays(trialInvite.trialDays)} okresu próbnego.`
                  : "Rejestrujesz się z zaproszenia na okres próbny."}{" "}
                Po zakończeniu okresu próbnego nie wystawimy automatycznie
                proformy — zamówisz, kiedy zdecydujesz.
              </p>
              {validInvite?.emailBound && (
                <p>
                  Zaproszenie jest przypisane do konkretnego adresu e-mail —
                  zarejestruj się adresem, dla którego je wystawiono.
                </p>
              )}
            </div>
          )}
        </div>

        {/* AC-22: Stepper/progress bar */}
        <Stepper currentStep={currentStep} />

        {/* Step content */}
        <div className="bg-white shadow-lg rounded-lg p-8">
          {inviteProblem && (
            <TrialInviteProblem
              kind={inviteProblem}
              onContinueWithoutInvite={continueWithoutInvite}
            />
          )}

          {visibleStep === 1 && (
            <Step1Eligibility
              formData={formData}
              onChange={handleFieldChange}
              onNext={handleStep1Next}
              loading={loading}
              error={error}
              eligibilityError={eligibilityError}
              headingRef={stepHeadingRef}
            />
          )}

          {visibleStep === 2 && (
            <Step2CompanyData
              formData={formData}
              onChange={handleFieldChange}
              onNext={() => { analytics.trackStepComplete(2); goToStep(3); }}
              onBack={() => goToStep(1)}
              gusWarning={gusWarning}
              headingRef={stepHeadingRef}
            />
          )}

          {visibleStep === 3 && (
            <Step3AccountData
              formData={formData}
              onChange={handleFieldChange}
              onNext={() => { analytics.trackStepComplete(3); goToStep(4); }}
              onBack={() => goToStep(2)}
              headingRef={stepHeadingRef}
            />
          )}

          {visibleStep === 4 && (
            <Step4PlanSelection
              formData={formData}
              plans={plans}
              plansLoading={plansLoading}
              plansError={plansError}
              trialDurationDays={trialDurationDays}
              trialEligible={trialEligible}
              trialInvite={trialInvite}
              unavailableScheme={unavailableScheme}
              onChange={handleFieldChange}
              onNext={() => { analytics.trackStepComplete(4); goToStep(5); }}
              onBack={() => goToStep(3)}
              onRetryPlans={refetchPlans}
              headingRef={stepHeadingRef}
            />
          )}

          {visibleStep === 5 && (
            <Step5Summary
              formData={formData}
              plans={plans}
              onSubmit={handleFinalSubmit}
              onBack={() => goToStep(4)}
              onGoToStep={goToStep}
              loading={loading}
              error={error}
              headingRef={stepHeadingRef}
              termsUrl={brandTermsUrl}
              privacyUrl={brandPrivacyUrl}
              legalLoading={brandLegalLoading}
              trialInvite={trialInvite}
              onContinueWithoutInvite={continueWithoutInvite}
            />
          )}
        </div>
      </div>
    </div>
  );
};
